<script setup>
/**
 * Layout principal de l'application
 * Structure: Sidebar fixe à gauche + Header fixe en haut + Content scrollable
 */
import { computed } from 'vue'
import { RouterView } from 'vue-router'
import { useLayoutStore } from '@/stores/layout'
import AppSidebar from './AppSidebar.vue'
import AppHeader from './AppHeader.vue'

const layoutStore = useLayoutStore()

const layoutClasses = computed(() => ({
  'layout--sidebar-collapsed': layoutStore.isSidebarCollapsed,
  'layout--mobile-sidebar-open': layoutStore.isMobileSidebarOpen,
}))
</script>

<template>
  <div class="app-layout" :class="layoutClasses">
    <!-- Overlay mobile quand sidebar ouverte -->
    <div 
      v-if="layoutStore.isMobileSidebarOpen" 
      class="mobile-overlay"
      @click="layoutStore.closeMobileSidebar"
    />
    
    <!-- Sidebar fixe à gauche -->
    <AppSidebar class="app-sidebar" />
    
    <!-- Contenu principal -->
    <div class="main-wrapper">
      <!-- Header fixe en haut -->
      <AppHeader class="app-header" />
      
      <!-- Zone de contenu scrollable -->
      <main class="main-content">
        <div class="content-container">
          <RouterView />
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
.app-layout {
  display: flex;
  min-height: 100vh;
  background-color: var(--surface-ground);
  max-width: 100%;
  overflow-x: hidden;
}

.app-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: var(--sidebar-width);
  height: 100vh;
  z-index: var(--z-fixed);
  transition: transform var(--transition-normal), width var(--transition-normal);
}

.main-wrapper {
  flex: 1;
  margin-left: var(--sidebar-width);
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  /* Évite que le contenu large fasse grossir le wrapper (flex min-width:auto) */
  min-width: 0;
  transition: margin-left var(--transition-normal);
}

.app-header {
  position: fixed;
  top: 0;
  right: 0;
  left: var(--sidebar-width);
  height: var(--header-height);
  z-index: var(--z-sticky);
  transition: left var(--transition-normal);
}

.main-content {
  flex: 1;
  margin-top: var(--header-height);
  padding: 0;
  overflow-x: hidden;
}

.content-container {
  padding: var(--space-6);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.mobile-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  /* Juste SOUS la sidebar (--z-fixed = 300) pour ne pas bloquer ses clics,
     mais au-dessus du header (--z-sticky) et du contenu. */
  z-index: calc(var(--z-fixed) - 1);
}

/* État sidebar collapsed (desktop) */
.layout--sidebar-collapsed .app-sidebar {
  width: var(--sidebar-collapsed-width);
}

.layout--sidebar-collapsed .main-wrapper {
  margin-left: var(--sidebar-collapsed-width);
}

.layout--sidebar-collapsed .app-header {
  left: var(--sidebar-collapsed-width);
}

/* Responsive mobile */
@media (max-width: 1024px) {
  .app-sidebar {
    transform: translateX(-100%);
  }
  
  .main-wrapper {
    margin-left: 0;
  }
  
  .app-header {
    left: 0;
  }
  
  .layout--mobile-sidebar-open .app-sidebar {
    transform: translateX(0);
  }
  
  .mobile-overlay {
    display: block;
  }
}

/* Petit mobile : réduit les marges latérales (le padding réel est porté par
   .content-container, pas .main-content). */
@media (max-width: 640px) {
  .content-container {
    padding: var(--space-3);
  }
}
</style>
